/**
 * WeblabsContent — main content for the Weblabs dashboard (presentational).
 *
 * Consumes SERVER-SIDE aggregates from useWeblabsData (via WeblabsDashboard):
 * a per-day impact split (pos/neg + annualized), KPI scalars, and filter
 * options. It renders the SB/SP toggle + client filters (which drive re-queries
 * upstream), KPI tiles, the diverging net-impact chart (green above zero, red
 * below, stepped Net line), and — fetched ON DEMAND — a per-day/bucket weblab
 * breakdown and a single-weblab drill-in. No raw rows are held client-side, so
 * nothing is ever truncated for long ranges / many weblabs.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  convertScaleToSuffix,
  determineScale,
} from "../../utils/reportGenerator";
import {
  bucketKey,
  bucketLabel,
  bucketRange,
  bucketSeries,
  formatSignedUsd,
  OTHER_WEBLAB,
  type DailyPoint,
  type Grouping,
  type WeblabMetric,
} from "./weblabsChart";
import type {
  BreakdownEntry,
  ChartPoint,
  DrillResult,
  WeblabsData,
} from "./useWeblabsData";

const METRIC_LABEL: Record<WeblabMetric, string> = {
  impact_sp_rev_day: "SP impact",
  impact_sb_rev_day: "SB impact",
};

/** The weblab's setup page — the weblab name is the URL slug. */
const WEBLAB_SETUP_URL = "https://app.weblab.amazon.dev/setup/";
const weblabUrl = (weblab: string) =>
  `${WEBLAB_SETUP_URL}${encodeURIComponent(weblab)}`;

/** Friendly labels for the raw activation_state values (value stays raw). */
const ACTIVATION_STATE_LABELS: Record<string, string> = {
  launch: "Launch",
  run: "Dialed Up",
};
const activationStateLabel = (v: string) => ACTIVATION_STATE_LABELS[v] ?? v;

/** The period word for the current grouping (used in column headers). */
const PERIOD_WORD: Record<Grouping, string> = {
  D: "day",
  W: "week",
  M: "month",
  Q: "quarter",
};

/** One categorical filter dropdown. */
const FilterSelect: React.FC<{
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  renderLabel?: (v: string) => string;
}> = ({ label, value, options, onChange, renderLabel }) => (
  <FormControl size="small" sx={{ minWidth: 150 }}>
    <InputLabel>{label}</InputLabel>
    <Select
      label={label}
      value={value}
      onChange={(e: SelectChangeEvent) => onChange(e.target.value)}
    >
      <MenuItem value="">All</MenuItem>
      {options.map((o) => (
        <MenuItem key={o} value={o}>
          {renderLabel ? renderLabel(o) : o}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

/** A single KPI tile. */
const KpiTile: React.FC<{
  label: string;
  value: string;
  sub?: React.ReactNode;
  color?: string;
}> = ({ label, value, sub, color }) => (
  <Card variant="outlined" sx={{ minWidth: 180, flex: "1 1 180px" }}>
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color }}>
        {value}
      </Typography>
      {sub && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ lineHeight: 1.35 }}
        >
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

/** Small label/value pair used in the drill-in header. */
const Detail: React.FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value ?? "—"}</Typography>
  </Box>
);

/** The draft (unapplied) filter values the toolbar edits. */
export interface WeblabsDraftFilters {
  metric: WeblabMetric;
  activationState: string;
  weblabType: string;
  runStart: string;
  runEnd: string;
}

export interface WeblabsContentProps {
  data: WeblabsData | null;
  isLoading: boolean;
  error: string | null;
  grouping: Grouping;
  /** The metric the CURRENT data reflects — used for rendering/labels. */
  appliedMetric: WeblabMetric;
  /** Draft filter values the toolbar edits; committed on Apply. */
  draft: WeblabsDraftFilters;
  onDraftChange: (partial: Partial<WeblabsDraftFilters>) => void;
  fetchBreakdown: (start: string, end: string) => Promise<BreakdownEntry[]>;
  fetchDrill: (weblab: string) => Promise<DrillResult>;
}

const WeblabsContent: React.FC<WeblabsContentProps> = ({
  data,
  isLoading,
  error,
  grouping,
  appliedMetric,
  draft,
  onDraftChange,
  fetchBreakdown,
  fetchDrill,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // Rendering reflects the APPLIED metric (what the fetched data is for); the
  // toolbar toggle edits draft.metric until the user clicks Apply.
  const metric = appliedMetric;
  const prod = metric === "impact_sp_rev_day" ? "SP" : "SB";

  const chart: ChartPoint[] = useMemo(() => data?.chart ?? [], [data]);
  const kpis = data?.kpis;
  const options = data?.options;

  // The clicked bucket → its breakdown; the selected weblab → its drill-in.
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<BreakdownEntry[] | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [selectedWeblab, setSelectedWeblab] = useState<string | null>(null);
  const [drill, setDrill] = useState<DrillResult | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const gridColor = theme.palette.divider;
  const axisColor = theme.palette.text.secondary;
  const netColor = isDark ? "#f5f5f5" : "#111827";
  const otherColor = isDark ? "#7a7a7a" : "#b0b0b0";
  // Diverging pair — reuses the KPI tiles' success/error colors; polarity is
  // ALSO encoded by position (above/below zero), so color is a reinforcing cue.
  const posColor = theme.palette.success.main;
  const negColor = theme.palette.error.main;

  // Chart series: for grouped views, sum the additive impact into buckets;
  // daily keeps the raw points (which also carry the annualized split).
  const chartSeries = useMemo<DailyPoint[]>(() => {
    if (grouping === "D") return chart as unknown as DailyPoint[];
    const mapped: DailyPoint[] = chart.map((p) => ({
      day: p.day,
      net: p.net,
      pos: p.pos,
      neg: p.neg,
    }));
    return bucketSeries(mapped, ["pos", "neg"], grouping);
  }, [chart, grouping]);

  // Daily annualized split for the tooltip (only meaningful in Daily view;
  // annualized is a per-weblab constant, so it can't be summed into buckets).
  const annByDay = useMemo(() => {
    const m = new Map<string, { pos: number; neg: number; net: number }>();
    for (const p of chart)
      m.set(p.day, { pos: p.annPos, neg: p.annNeg, net: p.annNet });
    return m;
  }, [chart]);

  const scale = useMemo(() => {
    let maxAbs = 1;
    for (const p of chartSeries) {
      maxAbs = Math.max(
        maxAbs,
        Math.abs(Number(p.pos ?? 0)),
        Math.abs(Number(p.neg ?? 0)),
        Math.abs(Number(p.net ?? 0))
      );
    }
    return determineScale(maxAbs);
  }, [chartSeries]);
  const scaleSuffix = convertScaleToSuffix(scale);

  // Fetch the clicked bucket's breakdown (re-runs if the filters/metric change
  // while a bucket is pinned, keeping it in sync).
  useEffect(() => {
    if (!selectedBucket) {
      setBreakdown(null);
      return;
    }
    let cancelled = false;
    setBreakdownLoading(true);
    const { start, end } = bucketRange(selectedBucket, grouping);
    fetchBreakdown(start, end)
      .then((rows) => {
        if (!cancelled) setBreakdown(rows);
      })
      .catch(() => {
        if (!cancelled) setBreakdown([]);
      })
      .finally(() => {
        if (!cancelled) setBreakdownLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBucket, grouping, fetchBreakdown]);

  // Fetch the selected weblab's drill-in series + attributes.
  useEffect(() => {
    if (!selectedWeblab) {
      setDrill(null);
      return;
    }
    let cancelled = false;
    setDrillLoading(true);
    fetchDrill(selectedWeblab)
      .then((res) => {
        if (!cancelled) setDrill(res);
      })
      .catch(() => {
        if (!cancelled) setDrill({ series: [], info: null });
      })
      .finally(() => {
        if (!cancelled) setDrillLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedWeblab, fetchDrill]);

  const drillSeries = useMemo(() => {
    const s = drill?.series ?? [];
    if (grouping === "D") return s;
    const byBucket = new Map<string, number>();
    for (const pt of s) {
      const k = bucketKey(pt.day, grouping);
      byBucket.set(k, (byBucket.get(k) ?? 0) + pt.value);
    }
    return Array.from(byBucket.entries())
      .map(([day, value]) => ({ day, value }))
      .sort((a, b) => a.day.localeCompare(b.day));
  }, [drill, grouping]);

  // Breakdown display: bucket net (from the chart point) + an "Other" rollup
  // for the weblabs beyond the top-N the query returned.
  const bucketNet = useMemo(() => {
    if (!selectedBucket) return 0;
    const p = chartSeries.find((x) => x.day === selectedBucket);
    return p ? Number(p.net ?? 0) : 0;
  }, [selectedBucket, chartSeries]);
  const breakdownOther = useMemo(() => {
    if (!breakdown) return 0;
    const shown = breakdown.reduce((s, e) => s + e.value, 0);
    return bucketNet - shown;
  }, [breakdown, bucketNet]);

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        {error}
      </Alert>
    );
  }
  if (isLoading && chart.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!isLoading && chart.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        No weblab impact data for the selected filters.
      </Alert>
    );
  }

  const renderChartTooltip = (props: any) => {
    const { active, payload, label } = props;
    if (!active || !payload || payload.length === 0) return null;
    const valOf = (key: string) => {
      const p = payload.find((x: any) => x.dataKey === key);
      return p != null ? Number(p.value) : 0;
    };
    // Annualized only in Daily view (a per-weblab constant can't be summed
    // into weekly/monthly buckets without over-counting).
    const ann = grouping === "D" ? annByDay.get(String(label)) : undefined;
    const row = (
      text: string,
      value: number,
      color?: string,
      strong?: boolean
    ) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 3,
          color: color ?? "text.primary",
          fontWeight: strong ? 700 : 400,
        }}
      >
        <span>{text}</span>
        <span>{formatSignedUsd(value, 1)}</span>
      </Box>
    );
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          border: `1px solid ${gridColor}`,
          borderRadius: 1,
          p: 1,
          fontSize: 12,
          minWidth: 210,
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, display: "block", mb: 0.5 }}
        >
          {bucketLabel(String(label), grouping)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Impact ({prod})
        </Typography>
        {row("Positive", valOf("pos"), posColor)}
        {row("Negative", valOf("neg"), negColor)}
        {row("Net", valOf("net"), undefined, true)}
        {ann && (
          <Box sx={{ mt: 0.75, pt: 0.5, borderTop: `1px solid ${gridColor}` }}>
            <Typography variant="caption" color="text.secondary">
              Annualized ({prod})
            </Typography>
            {row("Positive", ann.pos, posColor)}
            {row("Negative", ann.neg, negColor)}
            {row("Net", ann.net, undefined, true)}
          </Box>
        )}
      </Box>
    );
  };

  const topSub = (
    t: { weblab: string; total: number; ann: number | null } | null
  ) => {
    if (!t) return undefined;
    return (
      <>
        <Box
          component="span"
          title={t.weblab}
          sx={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {t.weblab}
        </Box>
        {t.ann != null && (
          <Box component="span" sx={{ display: "block" }}>
            Annualized {formatSignedUsd(t.ann, 1)}
          </Box>
        )}
      </>
    );
  };

  // The single-weblab drill-in. Rendered ABOVE the day breakdown so clicking a
  // weblab in the list surfaces its detail without scrolling.
  const drillPanel = selectedWeblab ? (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          <a
            href={weblabUrl(selectedWeblab)}
            target="_blank"
            rel="noreferrer"
            style={{ color: theme.palette.primary.main }}
          >
            {selectedWeblab} ↗
          </a>
          {drillLoading ? "  (loading…)" : ""}
        </Typography>
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: "pointer" }}
          onClick={() => setSelectedWeblab(null)}
        >
          Clear
        </Typography>
      </Box>
      <Stack
        direction="row"
        spacing={3}
        sx={{ flexWrap: "wrap", rowGap: 1, mb: 1.5 }}
      >
        <Detail label="Type" value={drill?.info?.weblab_type ?? null} />
        <Detail label="Owner" value={drill?.info?.primary_owner ?? null} />
        <Detail
          label="Activation state"
          value={
            drill?.info?.activation_state
              ? activationStateLabel(drill.info.activation_state)
              : null
          }
        />
        <Detail
          label="Launch date"
          value={drill?.info?.weblab_launch_date ?? null}
        />
        <Detail
          label="Start date"
          value={drill?.info?.weblab_start_date ?? null}
        />
        <Detail
          label={`Annualized ${prod}`}
          value={
            drill?.info?.annualized != null
              ? formatSignedUsd(drill.info.annualized, 1)
              : null
          }
        />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {METRIC_LABEL[metric]} by {PERIOD_WORD[grouping]}
      </Typography>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart
          data={drillSeries}
          margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
        >
          <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
          <XAxis
            dataKey="day"
            tick={{ fill: axisColor, fontSize: 11 }}
            minTickGap={24}
            tickFormatter={(v: string) => bucketLabel(v, grouping)}
          />
          <YAxis
            tick={{ fill: axisColor, fontSize: 11 }}
            tickFormatter={(v: number) => formatSignedUsd(v, scale)}
            width={70}
          />
          <ReferenceLine y={0} stroke={axisColor} />
          <Tooltip
            contentStyle={{
              background: theme.palette.background.paper,
              border: `1px solid ${gridColor}`,
              fontSize: 12,
            }}
            labelFormatter={(l: any) => bucketLabel(String(l), grouping)}
            formatter={(value: any) => formatSignedUsd(Number(value), 1)}
          />
          <Line
            type="stepAfter"
            dataKey="value"
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  ) : null;

  return (
    <Box>
      {isLoading && chart.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Refreshing…
          </Typography>
        </Box>
      )}

      {/* Toolbar: metric toggle + categorical filters (drive re-queries) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={draft.metric}
          onChange={(_, v) => v && onDraftChange({ metric: v as WeblabMetric })}
        >
          <ToggleButton value="impact_sp_rev_day">SP</ToggleButton>
          <ToggleButton value="impact_sb_rev_day">SB</ToggleButton>
        </ToggleButtonGroup>
        <FilterSelect
          label="Activation state"
          value={draft.activationState}
          options={options?.activationStates ?? []}
          onChange={(v) => onDraftChange({ activationState: v })}
          renderLabel={activationStateLabel}
        />
        <FilterSelect
          label="Weblab type"
          value={draft.weblabType}
          options={options?.weblabTypes ?? []}
          onChange={(v) => onDraftChange({ weblabType: v })}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Weblab Start / Launch Date
          </Typography>
          <TextField
            label="From"
            type="date"
            size="small"
            value={draft.runStart}
            onChange={(e) => onDraftChange({ runStart: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={draft.runEnd}
            onChange={(e) => onDraftChange({ runEnd: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          Changes take effect when you click <strong>Apply</strong> (top).
        </Typography>
      </Box>

      {/* KPI tiles */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
        <KpiTile
          label={`Net ${METRIC_LABEL[metric]} (range)`}
          value={formatSignedUsd(kpis?.net ?? 0, 1)}
          color={
            (kpis?.net ?? 0) >= 0
              ? theme.palette.success.main
              : theme.palette.error.main
          }
        />
        <KpiTile
          label={`Net ${prod} annualized (range)`}
          value={formatSignedUsd(kpis?.annNet ?? 0, 1)}
          color={
            (kpis?.annNet ?? 0) >= 0
              ? theme.palette.success.main
              : theme.palette.error.main
          }
        />
        <KpiTile
          label="Top positive (range)"
          value={
            kpis?.topPositive ? formatSignedUsd(kpis.topPositive.total, 1) : "—"
          }
          sub={topSub(kpis?.topPositive ?? null)}
          color={theme.palette.success.main}
        />
        <KpiTile
          label="Top negative (range)"
          value={
            kpis?.topNegative ? formatSignedUsd(kpis.topNegative.total, 1) : "—"
          }
          sub={topSub(kpis?.topNegative ?? null)}
          color={theme.palette.error.main}
        />
      </Stack>

      {/* Net daily-impact chart */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Net {METRIC_LABEL[metric]} by {PERIOD_WORD[grouping]}
          {scaleSuffix ? ` (${scaleSuffix})` : ""}
        </Typography>
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart
            data={chartSeries}
            stackOffset="sign"
            margin={{ top: 8, right: 16, bottom: 24, left: 8 }}
            onClick={(e: any) => {
              const d = e?.activeLabel;
              if (d) setSelectedBucket(String(d));
            }}
          >
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              tick={{ fill: axisColor, fontSize: 10 }}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={64}
              tickFormatter={(v: string) => bucketLabel(v, grouping)}
            />
            <YAxis
              tick={{ fill: axisColor, fontSize: 11 }}
              tickFormatter={(v: number) => formatSignedUsd(v, scale)}
              width={70}
            />
            <ReferenceLine y={0} stroke={axisColor} />
            <Tooltip content={renderChartTooltip} />
            <Legend
              verticalAlign="top"
              height={28}
              wrapperStyle={{ paddingBottom: 8 }}
            />
            <Bar
              dataKey="pos"
              name="Positive"
              stackId="impact"
              fill={posColor}
              minPointSize={2}
              isAnimationActive={false}
            />
            <Bar
              dataKey="neg"
              name="Negative"
              stackId="impact"
              fill={negColor}
              minPointSize={2}
              isAnimationActive={false}
            />
            <Line
              type="stepAfter"
              dataKey="net"
              name="Net"
              stroke={netColor}
              strokeWidth={2.5}
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <Typography variant="caption" color="text.secondary">
          Green = sum of positive weblabs that {PERIOD_WORD[grouping]}; red =
          sum of negative; the line is the Net. Click a bar to list that{" "}
          {PERIOD_WORD[grouping]}'s weblabs below (winners first) — each shows
          its impact for the {PERIOD_WORD[grouping]} and its annualized impact —
          then click a weblab to drill into its details, or its ↗ to open its
          page.
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 1, pt: 1, borderTop: `1px solid ${gridColor}` }}
        >
          Note: figures are estimates, not measured daily revenue (USD). Each
          day's number is a weblab's projected yearly revenue impact, sliced
          down to the day based on how much of the year's shopping traffic
          happened that day — and, for still-running experiments, scaled to the
          share of shoppers actually in the test.
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 0.75,
            fontFamily: "monospace",
            fontSize: "0.72rem",
            color: "text.secondary",
            whiteSpace: "pre-wrap",
          }}
        >
          {`SB Impact by Day = (traffic_daily / tot_traffic) × sb_rev_annualized
SP Impact by Day = (traffic_daily / tot_traffic) × sp_rev_annualized`}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 0.5 }}
        >
          where traffic_daily = that day's search traffic and tot_traffic = the
          marketplace's forecasted total search traffic for the year. In plain
          terms: impact for the day = annualized impact × (that day's search
          traffic ÷ the year's forecast search traffic).
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 0.75 }}
        >
          A weblab is dropped if its result isn't statistically solid, it has a
          data-quality alarm or too little data, it isn't a genuine experiment
          (or is a known test weblab), we're missing the traffic/allocation data
          needed to compute a daily value, or it falls outside the 12-month
          window (only weblabs with activity in the last 12 months are shown).
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 0.75, fontStyle: "italic" }}
        >
          Data limitations: figures come from the weblab-science team's source
          data, which has known accuracy limitations — a weblab can map to more
          than one experiment (APT), and SP vs. SB is identified by text
          matching. Treat these numbers as{" "}
          <strong>directionally correct, not dollar-accurate</strong>: use them
          to see relative impact and trends, not as an authoritative revenue
          figure. For SPB Finance internal use only.
        </Typography>
      </Paper>

      {/* Weblab drill-in — above the breakdown so it needs no scrolling. */}
      {drillPanel}

      {/* Click-to-pin day breakdown */}
      {selectedBucket && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Weblabs · {bucketLabel(selectedBucket, grouping)} · Net{" "}
              {formatSignedUsd(bucketNet, 1)}
              {breakdownLoading ? " · loading…" : ""}
            </Typography>
            <Typography
              variant="caption"
              color="primary"
              sx={{ cursor: "pointer" }}
              onClick={() => setSelectedBucket(null)}
            >
              Clear
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto auto",
              columnGap: 3,
              rowGap: 0.5,
              maxWidth: 760,
              fontSize: 13,
              alignItems: "center",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              Weblab
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, textAlign: "right" }}
            >
              Impact ({PERIOD_WORD[grouping]})
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, textAlign: "right" }}
            >
              Annualized ({prod})
            </Typography>

            {(breakdown ?? []).map((e) => (
              <React.Fragment key={e.weblab}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="span"
                    onClick={() => setSelectedWeblab(e.weblab)}
                    sx={{
                      color: "primary.main",
                      cursor: "pointer",
                      textDecoration: "underline",
                      "&:hover": { textDecoration: "none" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {e.weblab}
                  </Box>
                  <a
                    href={weblabUrl(e.weblab)}
                    target="_blank"
                    rel="noreferrer"
                    title="Open weblab page"
                    style={{
                      color: theme.palette.text.secondary,
                      textDecoration: "none",
                    }}
                  >
                    ↗
                  </a>
                </Box>
                <Box
                  sx={{
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    color:
                      e.value >= 0
                        ? theme.palette.success.main
                        : theme.palette.error.main,
                  }}
                >
                  {formatSignedUsd(e.value, 1)}
                </Box>
                <Box
                  sx={{
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    color:
                      e.ann == null
                        ? "text.secondary"
                        : e.ann >= 0
                        ? theme.palette.success.main
                        : theme.palette.error.main,
                  }}
                >
                  {e.ann == null ? "—" : formatSignedUsd(e.ann, 1)}
                </Box>
              </React.Fragment>
            ))}

            {Math.abs(breakdownOther) > 0.5 && (
              <>
                <Box component="span" sx={{ color: otherColor }}>
                  {OTHER_WEBLAB} (remaining weblabs)
                </Box>
                <Box
                  sx={{
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    color: otherColor,
                  }}
                >
                  {formatSignedUsd(breakdownOther, 1)}
                </Box>
                <Box sx={{ textAlign: "right", color: otherColor }}>—</Box>
              </>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default WeblabsContent;
